import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Grid,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { store } from './store';
import ProductCard from './components/ProductCard';
import ProductDetails from './pages/ProductDetails';
import Hero from './components/Hero';
import Layout from './components/Layout';
import { Product } from './types';
import Auth from './pages/Auth';
import Checkout from './pages/Checkout';
import { productService } from './services/productService';
import CartReview from './pages/CartReview';
import OrderDetails from './pages/OrderDetails';
import AdminDashboard from './pages/AdminDashboard';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import AdminLogin from './pages/AdminLogin';
import { ProtectedRoute } from './components/ProtectedRoute';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import ConditionsUtilisation from './pages/ConditionsUtilisation';

const HomePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const categories = useMemo(() => {
    const uniqueCategories = new Set(products.map((product: Product) => product.category).filter(Boolean));
    return Array.from(uniqueCategories) as string[];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];
    if (searchTerm.trim() !== '') {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(q) ||
          (product.description || '').toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((product) => product.category === selectedCategory);
    }
    return filtered;
  }, [searchTerm, selectedCategory, products]);

  const handleViewDetails = (product: Product) => {
    navigate(`/products/${product._id}`);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await productService.getAllProducts();
        setProducts(response);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <>
      <Hero />

      <Box
        id="catalogue"
        component="section"
        sx={{
          pt: { xs: 4, md: 6 },
          pb: { xs: 6, md: 8 },
          scrollMarginTop: '80px',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }} className="hg-fade-up">
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontSize: { xs: '1.6rem', md: '2rem' },
              }}
            >
              Notre catalogue
            </Typography>
            <Typography color="text.secondary">
              Choisissez vos pâtes et passez commande en quelques clics.
            </Typography>
          </Box>

          <TextField
            fullWidth
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              backgroundColor: '#fff',
              borderRadius: 999,
              '& .MuiOutlinedInput-root': {
                borderRadius: 999,
                '& fieldset': {
                  borderColor: 'rgba(26,20,18,0.1)',
                },
              },
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 3.5 }}
          >
            <Chip
              label="Tous"
              clickable
              onClick={() => setSelectedCategory('all')}
              color={selectedCategory === 'all' ? 'primary' : 'default'}
              variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                clickable
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selectedCategory === 'all' ? 'Tous les produits' : selectedCategory}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loading ? '…' : `${filteredProducts.length} produit${filteredProducts.length > 1 ? 's' : ''}`}
            </Typography>
          </Box>

          {!loading && filteredProducts.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">Aucun produit trouvé.</Typography>
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {filteredProducts.map((product, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={4}
                  lg={3}
                  key={product._id}
                  sx={{
                    animation: 'hg-fade-up 0.55s ease both',
                    animationDelay: `${Math.min(index, 8) * 0.05}s`,
                  }}
                >
                  <ProductCard product={product} onViewDetails={handleViewDetails} />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/" element={<Layout><Outlet /></Layout>}>
            <Route index element={<HomePage />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/checkout/review" element={<CartReview />} />
            <Route path="/order/:id" element={<OrderDetails />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/auth"
            element={
              <Layout>
                <Auth />
              </Layout>
            }
          />
          <Route
            path="/checkout/info"
            element={
              <Layout>
                <Checkout />
              </Layout>
            }
          />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/conditions-utilisation" element={<ConditionsUtilisation />} />
        </Routes>
      </Box>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <WhatsAppButton />}
    </Box>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <AppShell />
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
